// 通用 useMutation 包装 — 2026-10-03 Phase 12 新增
// 2026-10-17 U4 易用性扩展:
//   - 5xx(在线)→ 自动跳出友好 Modal 带 [重试] [取消] 按钮
//   - 离线(navigator.onLine=false / 无 response)→ 入队等网络恢复自动重试 + 顶部 toast
//   - 4xx(用户输入错)→ 仍走 message.error(retry 没意义)
//
// 2026-10-17 易用性 A-5 扩展:
//   - undoable: true → 成功 message 弹 5 秒带"撤销"按钮,点撤销调 onUndo(data, variables)
//   - undoConfig.successText / undoText / onUndo 都可选
//
// 之前每个 mutation 都要写:
//   useMutation({
//     mutationFn: (data) => axios.post('/api/xxx', { ...data, project_id: pid }),
//     onSuccess: () => { messageApi.success('成功'); queryClient.invalidateQueries(...) },
//     onError: (e) => messageApi.error(getErrorMessage(e))
//   })
//
// 用 useApiMutation 后:
//   const { mutate, isPending } = useApiMutation(
//     (data) => axios.post('/api/xxx', { ...data, project_id: pid }),
//     { successMsg: '成功', invalidateKeys: [['xxx-list', pid]] }
//   )
//
// 可撤销 mutation:
//   const createMutation = useApiMutation(
//     (data) => axios.post('/api/xxx', data),
//     { successMsg: '已创建', undoable: true,
//       undoConfig: { onUndo: (created) => deleteMutation.mutateAsync(created.id) } }
//   )

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMessage } from './useMessage'
import { useFriendlyError } from '../contexts/FriendlyErrorContext'
import { enqueueOfflineRequest } from './useOfflineQueue'
import { getErrorMessage } from '../utils'
import { showUndoableSuccess } from './useApiMutationUndo.jsx'

function getHttpStatus(err) {
  return err?.response?.status ?? null
}

function getRid(err) {
  return err?._rid || err?.response?.headers?.['x-request-id'] || ''
}

function shouldShowFriendlyModal(err) {
  const status = getHttpStatus(err)
  if (status && status >= 500) return true
  if (!status) return true  // 网络错误(无 response):超时、断网
  return false
}

function isLikelyOffline() {
  if (typeof navigator === 'undefined') return false
  return !navigator.onLine
}

export function useApiMutation(mutationFn, options = {}) {
  const {
    successMsg,
    invalidateKeys = [],
    friendlyError = true,
    // 2026-10-17 A-5:成功 message 附带撤销按钮
    undoable = false,
    undoConfig,
    onSuccess,
    onError,
    ...restOptions
  } = options
  const queryClient = useQueryClient()
  const messageApi = useMessage()
  const friendly = useFriendlyError()

  return useMutation({
    mutationFn,
    ...restOptions,
    onSuccess: (data, variables, context) => {
      // A-5: undoable=true → 弹带撤销按钮的成功 message
      // undoable 模式下,successText 来自 undoConfig.successText 优先(便于自定义文案),
      // 没有则回退到 successMsg
      if (undoable) {
        const text = undoConfig?.successText || successMsg
        if (text) showUndoableSuccess(messageApi, text, undoConfig, data, variables)
      } else if (successMsg) {
        messageApi.success(successMsg)
      }
      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key })
      }
      onSuccess?.(data, variables, context)
    },
    onError: (err, variables, context) => {
      // 1. 离线(navigator.onLine=false 且 mutation 没拿到 response)→ 进队列等恢复自动重试
      if (friendlyError && shouldShowFriendlyModal(err) && isLikelyOffline()) {
        enqueueOfflineRequest({
          name: successMsg ? successMsg.replace('成功', '') : '操作',
          retry: () => mutationFn(variables),
        })
        messageApi.warning('网络已断开,请求已排队,恢复后自动重试', 4)
      } else if (friendlyError && shouldShowFriendlyModal(err)) {
        // 2. 在线但服务端 5xx → 友好 Modal + 手动重试
        friendly.showError({
          title: '操作未完成',
          message: getErrorMessage(err, '网络异常,请稍后重试'),
          requestId: getRid(err),
          status: getHttpStatus(err),
          retryLabel: '重试',
          onRetry: () => mutationFn(variables),
        })
      } else {
        // 3. 4xx 校验错 → 走 toast(用户输入问题,retry 没意义)
        messageApi.error(getErrorMessage(err))
      }
      onError?.(err, variables, context)
    },
  })
}